import { useState } from "react";
import type {
  ActionRecord,
  AgentActionReceipt,
  AgentAttestation,
  EvidenceBlob,
  WitnessAttestation
} from "@sequesign/lib/types";
import { agentAttestationMessage, witnessAttestationMessage } from "@sequesign/lib/messages";
import { verifyEd25519 } from "@sequesign/lib/keys";

interface ActionTimelineProps {
  actions: ActionRecord[];
  evidence: Map<string, EvidenceBlob>;
  receipt: AgentActionReceipt | null;
}

interface ActionVerification {
  agentSignatureVerified: boolean | null;
  witnessVerified: boolean | null;
}

function findAttestations(
  receipt: AgentActionReceipt | null,
  action: ActionRecord
): {
  agent: AgentAttestation | null;
  witness: WitnessAttestation | null;
} {
  if (!receipt) return { agent: null, witness: null };
  return {
    agent: receipt.agent_attestations.find((a) => a.sequence === action.sequence) ?? null,
    witness: receipt.witness_attestations.find((w) => w.sequence === action.sequence) ?? null
  };
}

function verifyActionAttestations(
  action: ActionRecord,
  agent: AgentAttestation | null,
  witness: WitnessAttestation | null
): ActionVerification {
  const agentSignatureVerified = agent
    ? verifyEd25519(
        agent.agent_public_key,
        agentAttestationMessage({
          chainId: agent.chain_id,
          sequence: agent.sequence,
          actionRecordHash: agent.action_record_hash,
          chainState: agent.chain_state
        }),
        agent.signature
      )
    : null;
  const witnessVerified = witness
    ? verifyEd25519(
        witness.witness_public_key,
        witnessAttestationMessage({
          witnessId: witness.witness_id,
          chainId: witness.chain_id,
          sequence: witness.sequence,
          actionRecordHash: witness.action_record_hash,
          previousChainState: witness.previous_chain_state,
          chainState: witness.chain_state,
          witnessedAt: witness.witnessed_at
        }),
        witness.signature
      )
    : null;
  void action;
  return { agentSignatureVerified, witnessVerified };
}

function statusPill(value: boolean | null): JSX.Element {
  if (value === true) {
    return <span className="attest-pill verified">verified</span>;
  }
  if (value === false) {
    return <span className="attest-pill failed">failed</span>;
  }
  return <span className="attest-pill missing">missing</span>;
}

function summarizeEvidence(blob: EvidenceBlob | undefined): string {
  if (!blob) return "evidence missing";
  const content = blob.content as Record<string, unknown> | undefined;
  if (!content || typeof content !== "object") return "structured evidence";
  const keys = Object.keys(content).filter((k) => k !== "unsupported_claims");
  if (keys.length === 0) return "structured evidence";
  const preferred = keys.find((k) =>
    [
      "summary",
      "decision",
      "recommendation",
      "title",
      "description",
      "task_description",
      "review_summary"
    ].includes(k)
  );
  const key = preferred ?? keys[0];
  const value = (content as Record<string, unknown>)[key];
  if (typeof value === "string") {
    const trimmed = value.replace(/\s+/g, " ").trim();
    return `${key}: ${trimmed.length > 90 ? trimmed.slice(0, 87) + "..." : trimmed}`;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return `${key}: ${value}`;
  }
  return `${keys.length} field${keys.length === 1 ? "" : "s"}`;
}

function formatTimestamp(value: string): string {
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toISOString().replace("T", " ").slice(0, 19) + "Z";
  } catch {
    return value;
  }
}

const VERIFIABILITY_LABELS: Record<string, string> = {
  deterministic: "deterministic",
  non_deterministic: "non-deterministic",
  counterparty_attested: "counterparty-attested",
  tool_captured: "tool-captured",
  human_signed: "human-signed",
  unknown: "unknown"
};

export function ActionTimeline({ actions, evidence, receipt }: ActionTimelineProps) {
  const [expanded, setExpanded] = useState<number | null>(null);

  if (actions.length === 0) {
    return <div className="empty-state">This receipt has no recorded actions to display.</div>;
  }

  return (
    <div className="timeline">
      {actions.map((action) => {
        const { agent, witness } = findAttestations(receipt, action);
        const verification = verifyActionAttestations(action, agent, witness);
        const blob = evidence.get(action.action_id);
        const isOpen = expanded === action.sequence;
        const klass =
          VERIFIABILITY_LABELS[action.verifiability_class] ?? action.verifiability_class;
        return (
          <div className="action" key={action.sequence}>
            <button
              type="button"
              className="action-head"
              onClick={() => setExpanded(isOpen ? null : action.sequence)}
              aria-expanded={isOpen}
            >
              <div className="action-sequence">{String(action.sequence).padStart(3, "0")}</div>
              <div className="action-main">
                <div className="action-type">{action.action_type}</div>
                <div className="action-summary">{summarizeEvidence(blob)}</div>
              </div>
              <div className="action-meta">
                <div className="action-meta-line">
                  <span className="action-class">{klass}</span>
                </div>
                <div className="action-meta-line">
                  <span>{formatTimestamp(action.timestamp)}</span>
                </div>
                <div className="action-meta-line">
                  <span>agent {statusPill(verification.agentSignatureVerified)}</span>
                  <span>witness {statusPill(verification.witnessVerified)}</span>
                </div>
              </div>
            </button>

            {isOpen ? (
              <div className="action-detail">
                <h4>Action record</h4>
                <pre className="mono-block">{JSON.stringify(action, null, 2)}</pre>

                <h4>Evidence</h4>
                {blob ? (
                  <pre className="mono-block">{JSON.stringify(blob, null, 2)}</pre>
                ) : (
                  <div className="empty-state">Evidence file is missing from the package.</div>
                )}

                <h4>Agent attestation</h4>
                {agent ? (
                  <div className="kv-table">
                    <div className="kv-key">agent_id</div>
                    <div className="kv-value">{agent.agent_id}</div>
                    <div className="kv-key">chain_state</div>
                    <div className="kv-value">{agent.chain_state}</div>
                    <div className="kv-key">action_record_hash</div>
                    <div className="kv-value">{agent.action_record_hash}</div>
                    <div className="kv-key">signature_alg</div>
                    <div className="kv-value">{agent.signature_alg}</div>
                    <div className="kv-key">signature</div>
                    <div className="kv-value">{agent.signature}</div>
                  </div>
                ) : (
                  <div className="empty-state">No agent attestation for this action.</div>
                )}

                <h4>Witness attestation</h4>
                {witness ? (
                  <div className="kv-table">
                    <div className="kv-key">witness_id</div>
                    <div className="kv-value">{witness.witness_id}</div>
                    <div className="kv-key">witnessed_at</div>
                    <div className="kv-value">{witness.witnessed_at}</div>
                    <div className="kv-key">chain_state</div>
                    <div className="kv-value">{witness.chain_state}</div>
                    <div className="kv-key">action_record_hash</div>
                    <div className="kv-value">{witness.action_record_hash}</div>
                    <div className="kv-key">signature_alg</div>
                    <div className="kv-value">{witness.signature_alg}</div>
                    <div className="kv-key">signature</div>
                    <div className="kv-value">{witness.signature}</div>
                  </div>
                ) : (
                  <div className="empty-state">No witness attestation for this action.</div>
                )}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
